import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Organization } from '../../models/organization';
import { SiteUpdate } from '../../models/site-update';
import { IncadaLocation } from '../../models/incada-location';
import { OrganizationService } from '../../services/organization.service';
import { SalesforceService } from '../../services/salesforce.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { LaunchDarklyService } from 'src/app/shared/services/launch-darkly.service';
import { FleetManagementService } from 'src/app/internal-user/fleet-management/services/fleet-management.service';
import { AutoCompleteDropDownValidators } from 'src/app/shared/directives/validate-selectedvalue-in-collection';
import { AutoCompleteField } from 'src/app/shared/models/auto-complete-field';
import * as constants from '../../../../config/app-constants';

export interface EditSiteDialogData {
  organization: Organization;
  parentOrg: Organization;
}

@Component({
  selector: 'app-edit-site-dialog',
  templateUrl: './edit-site-dialog.component.html',
  styleUrls: ['./edit-site-dialog.component.scss'],
  standalone: false
})
export class EditSiteDialogComponent implements OnInit, OnDestroy {

  organization: Organization;
  parentOrg: Organization;
  formGroup: FormGroup;

  // Salesforce autocomplete
  siteOptionswithfilter: any[] = [];
  existingSites: any[];
  siteSalesforceId: string;
  siteSalesforceName: string;
  siteCustomerNumber: string;

  // Incada
  showIncadaControls = false;
  parentIncadaTenantSlug = '';
  parentIncadaRegionSlug = '';
  private incadaFlagEnabled = false;
  private selectedIncadaSite: IncadaLocation | null = null;
  private selectedIncadaOrgUnit: IncadaLocation | null = null;

  // Locking
  fieldsLocked = false;
  incadaEnabled = false;
  deviceCheckComplete = false;
  lockTooltip = 'Some fields are locked; Stratus has at least one device provisioned for the parent organization.';

  private flagSubscription: Subscription;

  constructor(
    public dialogRef: MatDialogRef<EditSiteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditSiteDialogData,
    private readonly organizationService: OrganizationService,
    private readonly salesforceService: SalesforceService,
    private readonly ngxLoader: NgxUiLoaderService,
    private readonly toastService: ToastService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly fleetManagementService: FleetManagementService,
  ) {}

  ngOnInit() {
    this.organization = this.data.organization;
    this.parentOrg = this.data.parentOrg;

    // Pre-populate Salesforce stored values
    this.siteSalesforceId = this.organization.salesforceId;
    this.siteSalesforceName = this.organization.salesforceName;
    this.siteCustomerNumber = this.organization.customerNo;

    // Seed the options list with the current site's Salesforce entry so the
    // autocomplete validator passes and displayWith resolves on initial load.
    if (this.siteSalesforceId) {
      this.siteOptionswithfilter = [{
        id: this.siteSalesforceId,
        name: this.siteSalesforceName,
        customer_Num__c: this.siteCustomerNumber,
        isOnboarded: false,
      }];
    }

    this.formGroup = new FormGroup({
      name: new FormControl(this.organization.name, [Validators.required]),
      salesforceName: new FormControl(this.siteSalesforceId || '', [
        Validators.required,
        AutoCompleteDropDownValidators.SelectedValueValidator(this.siteOptionswithfilter, AutoCompleteField.salesforce)
      ]),
    });

    this.loadExistingSites();

    // Salesforce autocomplete: search when user types 3+ chars
    this.formGroup.controls.salesforceName.valueChanges.pipe(
      switchMap(id => {
        if (id && id.length > 2) {
          return this.salesforceService.getSitesByNameOrCustomerNo(id);
        } else {
          this.siteOptionswithfilter = [];
          return [];
        }
      })
    ).subscribe(
      res => {
        res = this.disableExistingSites(res);
        this.siteOptionswithfilter = res;
        this.resetSalesforceValidators();
      },
      () => {
        this.siteOptionswithfilter = [];
        this.resetSalesforceValidators();
      }
    );

    // LaunchDarkly flag subscription
    this.flagSubscription = this.launchDarklyService.flagChange.subscribe((flags) => {
      this.incadaFlagEnabled = flags['release-incada'] ?? false;
      this.evaluateIncadaVisibility();
    });

    this.checkProvisionedDevices();
  }

  private evaluateIncadaVisibility() {
    const isIncadaOrg = !!(this.parentOrg?.incadaSlug);
    const shouldShow = this.incadaFlagEnabled && isIncadaOrg;

    if (shouldShow && !this.showIncadaControls) {
      this.showIncadaControls = true;
      this.parentIncadaTenantSlug = this.parentOrg.incadaSlug;
      this.parentIncadaRegionSlug = this.parentOrg.incadaRegion || '';

      // Add Incada form controls, pre-populated if site already has values
      this.formGroup.addControl(
        'incadaSite',
        new FormControl(this.organization.incadaName || '')
      );
      this.formGroup.addControl(
        'incadaOrgUnit',
        new FormControl(this.organization.incadaOrgUnitName || '')
      );

      this.selectedIncadaSite = null;
      this.selectedIncadaOrgUnit = null;
    } else if (!shouldShow && this.showIncadaControls) {
      this.showIncadaControls = false;
      this.formGroup.removeControl('incadaSite');
      this.formGroup.removeControl('incadaOrgUnit');
      this.selectedIncadaSite = null;
      this.selectedIncadaOrgUnit = null;
    }
  }

  private checkProvisionedDevices() {
    // Search at parent org/edge device level for provisioned devices, lock sensitive fields if anything is deployed for the org
    this.fleetManagementService.searchEdgeDevices({
      orgId: this.parentOrg.customerNo,
      enabled: true,
      provisioned: true,
    }).subscribe({
      next: (devices) => {
        if (devices && devices.length > 0) {
          this.fieldsLocked = true;
          this.formGroup.get('salesforceName').disable();

          if (this.organization.incadaId) {
            this.incadaEnabled = true;
          }
        }
        this.deviceCheckComplete = true;
      },
      error: (response) => {
        if (response?.status === 404) {
          // No devices found — fields stay unlocked
          this.deviceCheckComplete = true;
        }
      },
    });
  }

  ngOnDestroy() {
    if (this.flagSubscription) {
      this.flagSubscription.unsubscribe();
    }
  }

  // Salesforce autocomplete helpers

  fetchSalesforceDetails(siteDetail) {
    this.siteSalesforceId = siteDetail.id;
    this.siteSalesforceName = siteDetail.name;
    this.siteCustomerNumber = siteDetail.customer_Num__c;
  }

  getSiteName(value: any) {
    if (!value) return '';
    const site = this.siteOptionswithfilter.find(org => org.id === value);
    // Fall back to the stored Salesforce name when the ID isn't in the current filtered list
    return site ? site.name : (value === this.siteSalesforceId ? this.siteSalesforceName : value);
  }

  getSalesforceErrorMessage() {
    if (this.siteOptionswithfilter == null || this.siteOptionswithfilter.length === 0) {
      return 'No Site Name retrieved from Salesforce';
    } else if (this.formGroup.get('salesforceName').hasError('required')) {
      return 'Site Name required';
    } else if (this.formGroup.get('salesforceName').hasError('ShowValidatonError')) {
      return 'Site Name is invalid';
    }
    return null;
  }

  private loadExistingSites() {
    this.organizationService.getSites()
      .subscribe((sites) => {
        this.existingSites = sites;
      });
  }

  private disableExistingSites(assets) {
    assets?.forEach((element) => {
      // Exclude the current site so its own Salesforce entry stays selectable
      const system = this.existingSites?.find(site =>
        site.salesforceId === element.id && site.organizationId !== this.organization.organizationId
      );
      element.isOnboarded = system != null;
    });
    return assets;
  }

  private resetSalesforceValidators() {
    this.formGroup.controls.salesforceName.clearValidators();
    this.formGroup.controls.salesforceName.setValidators([
      Validators.required,
      AutoCompleteDropDownValidators.SelectedValueValidator(this.siteOptionswithfilter, AutoCompleteField.salesforce)
    ]);
  }

  // Incada location handlers

  onIncadaSiteSelected(location: IncadaLocation | null) {
    this.selectedIncadaSite = location;
  }

  onIncadaOrgUnitSelected(location: IncadaLocation | null) {
    this.selectedIncadaOrgUnit = location;
  }

  // Dialog actions

  closePopup(result?: string) {
    this.dialogRef.close(result);
  }

  save() {
    if (!this.deviceCheckComplete || !this.formGroup.valid) {
      return;
    }

    const siteUpdate: SiteUpdate = {
      name: this.formGroup.get('name').value,
      siteSalesforceId: this.siteSalesforceId ?? null,
      siteSalesforceName: this.siteSalesforceName ?? null,
      siteCustomerNo: this.siteCustomerNumber ?? null,
      incadaSiteId: this.selectedIncadaSite?.id ?? null,
      incadaSiteSlug: this.selectedIncadaSite?.identifier ?? null,
      incadaSiteName: this.selectedIncadaSite?.name ?? null,
      incadaOrgUnitId: this.selectedIncadaOrgUnit?.id ?? null,
      incadaOrgUnitSlug: this.selectedIncadaOrgUnit?.identifier ?? null,
      incadaOrgUnitName: this.selectedIncadaOrgUnit?.name ?? null,
    };

    this.ngxLoader.start('edit-site-submit');

    this.organizationService.updateSite(
      this.organization.parent_Account_Id,
      this.organization.organizationId,
      siteUpdate
    ).subscribe({
      next: () => {
        this.ngxLoader.stop('edit-site-submit');
        this.closePopup('success');
        setTimeout(() => {
          this.toastService.openToast('Site updated.', constants.ToastPanelType.done);
        }, 700);
      },
      error: (response) => {
        this.toastService.stopLoadingAndShowError(response, 'Error updating site.', 'edit-site-submit');
      },
    });
  }
}
